import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Icon from '../../../components/AppIcon';
import TaskCard from './TaskCard';

const QuadrantCard = ({ 
  quadrant, 
  tasks, 
  onAddTask, 
  onEditTask, 
  onDeleteTask, 
  onToggleComplete,
  onSwipeAction 
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: quadrant?.id,
  });

  const taskIds = tasks?.map(task => task?.id);

  const getQuadrantStyles = () => {
    const baseStyles = "rounded-xl border-2 transition-all duration-250";
    const hoverStyles = isOver ? "border-primary bg-primary/5 shadow-soft-lg" : "border-border bg-card";
    return `${baseStyles} ${hoverStyles}`;
  };

  return (
    <div className={getQuadrantStyles()}>
      {/* Quadrant Header */}
      <div className="p-4 md:p-5 lg:p-6 border-b border-border">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 md:gap-3">
            <div 
              className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: quadrant?.color + '20' }}
            >
              <Icon 
                name={quadrant?.icon} 
                size={20} 
                color={quadrant?.color}
              />
            </div>
            <div>
              <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground">
                {quadrant?.title}
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                {tasks?.length} {tasks?.length === 1 ? 'task' : 'tasks'}
              </p>
            </div>
          </div>
        </div>

        {/* Inline Add Task */}
        <button
          onClick={() => onAddTask(quadrant?.id)}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-all duration-250 group"
        >
          <Icon 
            name="Plus" 
            size={16} 
            color="var(--color-muted-foreground)"
            className="group-hover:text-primary transition-colors"
          />
          <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors">
            Add task
          </span>
        </button>
      </div>
      {/* Task List */}
      <div 
        ref={setNodeRef}
        className="p-3 md:p-4 lg:p-5 min-h-[200px] md:min-h-[300px] space-y-2 md:space-y-3"
      >
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          {tasks?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 md:py-12 text-center">
              <div 
                className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center mb-3 md:mb-4"
                style={{ backgroundColor: quadrant?.color + '10' }}
              >
                <Icon 
                  name="Inbox" 
                  size={32} 
                  color={quadrant?.color}
                />
              </div>
              <p className="text-sm md:text-base text-muted-foreground">
                No tasks yet
              </p>
              <p className="text-xs md:text-sm text-muted-foreground/70 mt-1">
                Add your first task to get started
              </p>
            </div>
          ) : (
            tasks?.map(task => (
              <TaskCard
                key={task?.id}
                task={task}
                quadrantColor={quadrant?.color}
                onEdit={onEditTask}
                onDelete={onDeleteTask}
                onToggleComplete={onToggleComplete}
                onSwipeAction={onSwipeAction}
              />
            ))
          )}
        </SortableContext>
      </div>
    </div>
  );
};

export default QuadrantCard;